import React, { useMemo, useState } from "react";
import { X, Check, Pencil, Sparkles } from "lucide-react";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const parseList = (str) => {
  const out = [];
  const seen = new Set();
  String(str || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .forEach((p) => {
      const k = p.toLowerCase();
      if (!seen.has(k)) {
        seen.add(k);
        out.push(p);
      }
    });
  return out;
};

const tokenize = (s) =>
  String(s || "")
    .split(/(\s+)/)
    .filter((t) => t.length > 0);

const wordSet = (s) =>
  new Set(
    String(s || "")
      .toLowerCase()
      .split(/\s+/)
      .map((w) => w.replace(/[^a-z0-9]/gi, ""))
      .filter(Boolean)
  );

// Lightweight word-level highlight: mark tokens that changed between the two
// versions. Not a true LCS diff, but clearly communicates what the AI altered.
function DiffText({ text, compareTo, mode }) {
  const otherSet = useMemo(() => wordSet(compareTo), [compareTo]);
  const tokens = useMemo(() => tokenize(text), [text]);
  return (
    <p className="text-sm leading-relaxed text-[#0F1B33] whitespace-pre-wrap break-words">
      {tokens.map((tok, i) => {
        if (/^\s+$/.test(tok)) return <span key={i}>{tok}</span>;
        const clean = tok.toLowerCase().replace(/[^a-z0-9]/gi, "");
        const changed = clean && !otherSet.has(clean);
        if (!changed) return <span key={i}>{tok}</span>;
        return (
          <span
            key={i}
            className={
              mode === "added"
                ? "bg-[#DCFCE7] text-[#166534] rounded px-0.5"
                : "bg-[#FEE2E2] text-[#991B1B] line-through rounded px-0.5"
            }
          >
            {tok}
          </span>
        );
      })}
    </p>
  );
}

// ---------------------------------------------------------------------------
// AIConfirmModal
// mode="text":   props { originalText, aiText }         -> onAccept(finalString)
// mode="fields": props { originalText, proposals[] }    -> onAccept({key:value})
//   proposals: [{ key, label, kind: "list"|"text", ai }]
// ---------------------------------------------------------------------------
export default function AIConfirmModal({
  open,
  mode = "text",
  title = "Review AI suggestion",
  originalText = "",
  aiText = "",
  proposals = [],
  onCancel,
  onAccept,
  busy = false,
}) {
  const [editing, setEditing] = useState(false);
  const [draftText, setDraftText] = useState(aiText || "");
  const [fieldDrafts, setFieldDrafts] = useState(() => {
    const fd = {};
    (proposals || []).forEach((p) => {
      fd[p.key] =
        p.kind === "list"
          ? (Array.isArray(p.ai) ? p.ai : []).join(", ")
          : String(p.ai || "");
    });
    return fd;
  });

  if (!open) return null;

  const handleAccept = () => {
    if (mode === "text") {
      onAccept(editing ? draftText : aiText || "");
      return;
    }
    const result = {};
    (proposals || []).forEach((p) => {
      const raw = fieldDrafts[p.key];
      result[p.key] = p.kind === "list" ? parseList(raw) : String(raw || "");
    });
    onAccept(result);
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel?.();
      }}
      data-testid="ai-confirm-modal"
    >
      <div className="bg-white w-full sm:max-w-3xl sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E2E8F0]">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#ECFDF3] text-[#15803D]">
              <Sparkles className="h-4 w-4" />
            </span>
            <h3 className="text-base font-bold text-[#0F1B33]">{title}</h3>
          </div>
          <button
            type="button"
            onClick={() => !busy && onCancel?.()}
            className="text-[#94A3B8] hover:text-[#0F1B33] p-1 rounded-md min-h-[44px] min-w-[44px] flex items-center justify-center"
            data-testid="ai-confirm-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 overflow-y-auto">
          <p className="text-xs text-[#64748B] mb-3">
            Nothing is saved until you press <strong>Accept</strong>. Review the AI
            output below, edit if needed, then accept or cancel.
          </p>

          {mode === "text" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-3">
                <div className="text-[11px] font-bold uppercase tracking-wide text-[#64748B] mb-2">
                  Your text
                </div>
                <DiffText text={originalText} compareTo={aiText} mode="removed" />
              </div>
              <div className="rounded-xl border border-[#BBF7D0] bg-[#F0FDF4] p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-[#15803D]">
                    AI version
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditing((v) => !v)}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0F1F3D] hover:underline"
                    data-testid="ai-confirm-edit-toggle"
                  >
                    <Pencil className="h-3 w-3" />
                    {editing ? "Preview" : "Edit"}
                  </button>
                </div>
                {editing ? (
                  <textarea
                    value={draftText}
                    onChange={(e) => setDraftText(e.target.value)}
                    rows={7}
                    maxLength={500}
                    className="w-full text-sm p-2 rounded-md border border-[#CBD5E1] bg-white focus:outline-none focus:ring-2 focus:ring-[#15803D]/30"
                    data-testid="ai-confirm-edit-textarea"
                  />
                ) : (
                  <DiffText text={aiText} compareTo={originalText} mode="added" />
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {originalText ? (
                <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-3">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-[#64748B] mb-1">
                    Your context
                  </div>
                  <p className="text-sm text-[#334155] whitespace-pre-wrap break-words">
                    {originalText}
                  </p>
                </div>
              ) : null}

              <div className="text-[11px] font-bold uppercase tracking-wide text-[#15803D]">
                AI-suggested fields — edit before accepting
              </div>

              {(proposals || []).length === 0 ? (
                <p className="text-sm text-[#64748B]">
                  The AI did not return anything new to fill.
                </p>
              ) : (
                <div className="space-y-3">
                  {proposals.map((p) => (
                    <div key={p.key}>
                      <label className="block text-xs font-semibold text-[#0F1B33] mb-1">
                        {p.label}
                        {p.kind === "list" ? (
                          <span className="ml-1 font-normal text-[#94A3B8]">
                            (comma-separated)
                          </span>
                        ) : null}
                      </label>
                      {p.kind === "list" ? (
                        <input
                          value={fieldDrafts[p.key] ?? ""}
                          onChange={(e) =>
                            setFieldDrafts((s) => ({ ...s, [p.key]: e.target.value }))
                          }
                          className="w-full text-sm px-3 py-2 rounded-md border border-[#CBD5E1] bg-white focus:outline-none focus:ring-2 focus:ring-[#15803D]/30"
                          data-testid={`ai-confirm-field-${p.key}`}
                        />
                      ) : (
                        <textarea
                          value={fieldDrafts[p.key] ?? ""}
                          onChange={(e) =>
                            setFieldDrafts((s) => ({ ...s, [p.key]: e.target.value }))
                          }
                          rows={3}
                          className="w-full text-sm px-3 py-2 rounded-md border border-[#CBD5E1] bg-white focus:outline-none focus:ring-2 focus:ring-[#15803D]/30"
                          data-testid={`ai-confirm-field-${p.key}`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-[#E2E8F0]">
          <button
            type="button"
            onClick={() => !busy && onCancel?.()}
            className="px-4 py-2.5 rounded-md border border-[#E2E8F0] text-sm font-medium text-[#475569] hover:bg-[#F8FAFC] min-h-[44px]"
            data-testid="ai-confirm-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAccept}
            disabled={busy}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#15803D] text-white text-sm font-semibold hover:bg-[#166534] disabled:opacity-50 min-h-[44px]"
            data-testid="ai-confirm-accept"
          >
            <Check className="h-4 w-4" />
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
